import { Input } from "@/components/ui/input";
import { AlertTriangle } from "lucide-react";

// Mirrors LiveRatiosPanel's dual-badge approach, plus a third independent "Staffing Not Set"
// state — a future date/time with zero staff coverage rows must never render a false ok/under.
const BADGE_STYLE = {
  full: "bg-[#C97B7B]/10 text-[#B85C5C] border-[#C97B7B]/30",
  under: "bg-[#D8A657]/10 text-[#B8873C] border-[#D8A657]/30",
  ok: "bg-[#5B9E7A]/10 text-[#3F7A5C] border-[#5B9E7A]/30",
  notSet: "bg-slate-100 text-slate-500 border-slate-200",
};
const BADGE_DOT = { full: "bg-[#C97B7B]", under: "bg-[#D8A657]", ok: "bg-[#5B9E7A]", notSet: "bg-slate-400" };
const BADGE_LABEL = { full: "Full", under: "Under Ratio", ok: "Ratio OK", notSet: "Staffing Not Set" };

export const ProjectedRatiosPanel = ({ date, onDateChange, rooms, sharedPools = [] }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="projected-ratios-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-slate-600">Projected Ratios for The Selected Date</h2>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={date}
            onChange={(e) => onDateChange(e.target.value)}
            className="w-36 h-8 text-xs"
            data-testid="projected-date-input"
          />
        </div>
      </div>

      {rooms.length === 0 && <p className="text-sm text-slate-300">No rooms set up yet.</p>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {rooms.map((r) => {
          const badges = [];
          if (r.isFull) badges.push("full");
          if (r.staffingNotSet) badges.push("notSet");
          else if (r.isUnderRatio) badges.push("under");
          else badges.push("ok");
          return (
            <div key={r.roomId} className="rounded-lg border border-slate-200 px-3 py-2.5" data-testid={`projected-room-card-${r.roomId}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                  {r.roomName}
                  {r.sharedStaff?.length > 1 && (
                    <span
                      className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-[#D9B36A] bg-[#FBF0D9] text-[#8A6A2B] font-semibold"
                      data-testid={`projected-shared-staff-count-${r.roomId}`}
                      title={`${r.sharedStaff.length} staff sharing this room`}
                    >
                      {r.sharedStaff.length} shared
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-1 flex-wrap justify-end" data-testid={`projected-room-badges-${r.roomId}`}>
                  {badges.map((status) => (
                    <span
                      key={status}
                      className={`flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full border ${BADGE_STYLE[status]}`}
                      data-testid={`projected-badge-${status}-${r.roomId}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${BADGE_DOT[status]}`} />
                      {BADGE_LABEL[status]}
                    </span>
                  ))}
                </span>
              </div>
              <div className="space-y-1.5">
                <div>
                  <p className="text-xs font-bold text-slate-700">Room Capacity</p>
                  <p className="text-xs text-slate-600">{r.childrenPresent} slots filled · {r.seatsAvailable} still available</p>
                  <p className="text-[10px] text-slate-400">Needs {r.staffNeededAtCapacity} staff at full capacity</p>
                  {r.guestChildren?.length > 0 && (
                    <p className="text-[11px] text-[#8A6A2B]" data-testid={`projected-guest-children-${r.roomId}`}>
                      +{r.guestChildren.length} {r.guestChildren[0].nativeCategoryLabel} (Room for Ratios)
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Current Staff</p>
                  <p className="text-xs text-slate-600">
                    {r.staffCovering} staff · Ratio 1:{r.effectiveRatio}
                    {r.effectiveRatio !== r.licensedRatio && (
                      <span className="text-[#B8873C]"> (dropped from 1:{r.licensedRatio})</span>
                    )}
                    {!r.staffingNotSet && r.isUnderRatio && (
                      <span className="text-[#B85C5C] font-semibold"> · Needs {r.requiredStaff - r.staffCovering} more</span>
                    )}
                    {!r.staffingNotSet && !r.isUnderRatio && r.requiredStaff === 0 && (
                      <span className="text-slate-400 font-semibold"> · No staff needed</span>
                    )}
                    {!r.staffingNotSet && !r.isUnderRatio && r.requiredStaff > 0 && (
                      <span className="text-[#3F7A5C] font-semibold"> · ✓ Staffed</span>
                    )}
                  </p>
                  {r.primaryStaff?.length > 0 && (
                    <p className="text-[11px] text-slate-500 truncate" data-testid={`covering-staff-${r.roomId}`}>
                      Covering: {r.primaryStaff.map((s) => s.name).join(", ")}
                    </p>
                  )}
                  {r.sharedStaff?.length > 0 && (
                    <p className="text-[11px] text-[#B8873C] truncate" data-testid={`projected-shared-staff-${r.roomId}`}>
                      {r.sharedStaff.map((s) => `${s.name} — ${s.coverageType === "same_time" ? "Shared Staff (Same Time)" : "Shared Staff"}`).join(", ")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {sharedPools.length > 0 && (
        <div className="mt-3 space-y-2">
          {sharedPools.map((p, idx) => (
            <div
              key={idx}
              className="flex items-start gap-2 rounded-lg border border-dashed border-[#D8A657]/50 bg-[#D8A657]/[0.06] px-3 py-2"
              data-testid={`projected-shared-pool-note-${idx}`}
            >
              <AlertTriangle className="w-4 h-4 text-[#B8873C] shrink-0 mt-0.5" />
              <div className="text-xs text-[#8a6529]">
                <span className="font-medium">
                  {p.staff.map((s) => s.name).join(", ")} — supervising {p.categoryLabels[0]} + {p.categoryLabels[1]} together:
                </span>{" "}
                combined ratio 1:{p.combinedRatio}, {p.combinedChildren} children sharing {p.combinedAllowance} total allowance —{" "}
                <strong className={p.combinedRemaining === 0 ? "text-[#B85C5C]" : "text-[#8a6529]"}>{p.combinedRemaining} open</strong>
                {p.isUnderRatio && <span className="font-semibold"> — Under Ratio</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
